import { Box, Flex, Icon, Text, Link as ChakraLink } from '@chakra-ui/react';
import { ArrowLeftIcon } from '@heroicons/react/solid';
import { NavigationConfiguration } from '@lib/docs/types';
import Link from 'next/link';
import { SidenavItem } from '../layout/sidenav-item';
import { useRouter } from 'next/router';
import { getNavigationContextByActivePath } from '@lib/docs/nav';
import { ROOT_DOCS_PATH } from '@lib/docs/constants';
import { NavigationItems } from './nav-items';
import { ICON_MAP } from './icons';
import { useEffect, useRef } from 'react';

/**
 * Accepts a navigation configuration object and renders a full navigation tree.
 */
export function Navigation({
  config,
  isMobileMenuOpen,
  handleCloseMobileMenu = () => void 0
}: {
  config: NavigationConfiguration;
  isMobileMenuOpen?: boolean;
  handleCloseMobileMenu?: () => void;
}) {
  const { asPath } = useRouter();
  const activePath = asPath.replace(/#.*/, ''); // Remove hash fragment

  const docsPathWithoutRoot = activePath.replace(ROOT_DOCS_PATH, '');
  const activeContext = getNavigationContextByActivePath(
    config,
    docsPathWithoutRoot
  );

  // TODO: come up with a more elegant solution here. This is preferable to passing down the handleCloseMobileMenu prop to every child, but it could still use improvement.
  const isRendered = useRef(false);
  useEffect(() => {
    if (!isRendered.current) {
      isRendered.current = true;
      return;
    }

    if (isMobileMenuOpen) {
      handleCloseMobileMenu();
    }
  }, [activePath]);

  return (
    <Box
      as="nav"
      h={{ base: 'auto', lg: 'full' }}
      w="full"
      pos="relative"
      overflowY="auto"
      overflowX="hidden"
    >
      {/* Parents view */}
      <Box
        opacity={!activeContext ? 1 : 0}
        position="absolute"
        top="0"
        transform={`translateX(${!activeContext ? 0 : -100}%)`}
        transition="all .3s"
        zIndex={!activeContext ? 1 : 0}
        w={{ base: 'full', sm: 'auto', lg: 'full' }}
        paddingRight={4}
        pb={{ base: 12, lg: 8 }}
        visibility={!activeContext ? 'visible' : 'hidden'}
      >
        <NavigationItems config={config} />
      </Box>

      {/* Children view */}
      <Box
        opacity={activeContext ? 1 : 0}
        position="absolute"
        top="0"
        transition="all .3s"
        transform={`translateX(${activeContext ? 0 : 100}%)`}
        zIndex={activeContext ? 1 : 0}
        w={{ base: 'full', sm: 'auto', lg: 'full' }}
        paddingRight={4}
        pb={{ base: 12, lg: 8 }}
        visibility={activeContext ? 'visible' : 'hidden'}
      >
        <ChakraLink as={Link} href={ROOT_DOCS_PATH}>
          <SidenavItem icon={ArrowLeftIcon} text="All docs" />
        </ChakraLink>

        {activeContext && (
          <>
            <Flex align="center" gap={3} px={3} py={5}>
              {activeContext?.[0]?.icon && (
                <Icon
                  as={ICON_MAP[activeContext?.[0]?.icon]}
                  boxSize={6}
                  color="var(--primaryTextColor)"
                  position="absolute"
                />
              )}

              <Text
                color="var(--primaryTextColor)"
                textStyle={
                  activeContext?.[0]?.title?.length > 20 ? 'md' : 'lg-medium'
                }
                ml={9}
              >
                {activeContext?.[0]?.title}
              </Text>
            </Flex>

            <Box pl={4}>
              <NavigationItems config={activeContext?.[1] ?? []} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
